<template>
    <layout>
        <div class="text-3xl mt-4 px-4 font-light flex items-end justify-between">
            <div data-cy="company-settings-user-title">{{ $t('globals.navigation.header.reports.title') }}</div>
            <a-checkbox
                @change="onCheckboxChange"
                v-if="isSiteAdmin"
                v-model:checked="include_inactive_reports"
                data-cy="flag-include-inactive"
            >
                {{ $t('report.inactive_check') }}
            </a-checkbox>
        </div>

        <div class="list-container">
            <div class="list-top-container">
                <div class="list-search-container w-full mb-2 md:w-auto md:mb-0">
                    <a-input-search data-cy="input-search" @search="onSearch" :placeholder="$t('report.form.search')" />
                </div>
                <div class="list-buttons w-full mb-2 md:w-auto md:mb-0">
                    <a-dropdown :trigger="['click']" v-if="recordsSelected && showDeleteButton" class="mr-4">
                        <a-button @click="(e) => e.preventDefault()" data-cy="btn-mass-selection">
                            {{ $t('report.table.buttons.actions') }}
                            <DownOutlined />
                        </a-button>
                        <template #overlay>
                            <a-menu @click="onActionButtons">
                                <a-menu-item key="delete" data-cy="actions-delete-report">
                                    {{ $t('report.table.buttons.delete') }}
                                </a-menu-item>
                            </a-menu>
                        </template>
                    </a-dropdown>
                    <a-button-group>
                        <a-button
                            @click="onAdd"
                            v-if="showCreateButton"
                            data-cy="btn-create-report-template"
                            type="primary"
                        >
                            {{ $t('report.table.buttons.add') }}
                        </a-button>
                        <a-button
                            @click="onExport"
                            v-if="showExportButton"
                            :loading="export_button_processing"
                            data-cy="btn-export-report-template"
                        >
                            {{ $t('report.table.buttons.export') }}
                        </a-button>
                    </a-button-group>
                </div>
            </div>
            <div class="list-table pt-2 md:pt-4" data-cy="table-report-templates">
                <report-template-table
                    ref="ReportTemplateTable"
                    :selected_row_keys="selected_row_keys"
                    @onSelectChange="onSelectChange"
                    @onTableAction="onTableAction"
                />
            </div>
        </div>

        <a-drawer
            :title="drawerTitle()"
            :width="500"
            :open="drawer_visible"
            :body-style="{ padding: '0px', paddingBottom: '10px' }"
            :destroyOnClose="true"
            :maskClosable="false"
            :rootClassName="'epic-' + moduleStateLink"
            @close="onDrawerClose"
        >
            <div v-if="mode == 'edit'">
                <form-report-template
                    :report_template="report_template"
                    :mode="mode"
                    :processing="processing"
                    :validation="validation"
                    @is-form-dirty="isFormDirty"
                    @formSubmit="onSave"
                    @onDrawerClose="onDrawerClose"
                />
            </div>

            <div v-if="mode == 'print'">
                <form-generate-report
                    :report_params="report_params"
                    :mode="mode"
                    :processing="processing"
                    :validation="params_validation"
                    @is-form-dirty="isFormDirty"
                    @formSubmit="onGenerateReport"
                    @onDrawerClose="onDrawerClose"
                />
            </div>
        </a-drawer>
    </layout>
</template>

<script>
import Layout from '@/layouts/Layout.vue';
import ReportTemplateTable from '@/components/tables/ReportTemplate.vue';
import FormReportTemplate from '@/components/forms/ReportTemplate.vue';
import FormGenerateReport from '@/components/forms/GenerateReport.vue';
import UiCheckbox from '@/components/ui/Checkbox.vue';

import User from '@/models/user.js';
import { DownOutlined } from '@ant-design/icons-vue';

export default {
    components: {
        Layout,
        ReportTemplateTable,
        FormReportTemplate,
        FormGenerateReport,
        UiCheckbox,
        DownOutlined,
    },
    computed: {
        showCreateButton() {
            return User.can('manage-report-template');
        },
        showDeleteButton() {
            return User.can('manage-report-template');
        },
        showExportButton() {
            return User.can('export-report-template');
        },
        recordsSelected() {
            return this.selected_row_keys.length > 0;
        },
        isSiteAdmin() {
            return User.can('manage-report-template');
        },
        moduleStateLink() {
            return this.$store.state.system.is_epicvault_enabled ? 'default' : 'claim';
        },
    },
    data() {
        return {
            data: [],
            search_value: '',
            report_template: {},
            report_params: {},
            mode: '',
            drawer_visible: false,
            validation: {
                errors: {},
                message: '',
            },
            params_validation: {
                errors: {},
                message: '',
            },
            drawer_form_dirty: false,
            filter_value: [],
            processing: false,
            export_button_processing: false,
            loading: false,
            selected_row_keys: [],
            include_inactive_reports: false,
        };
    },
    methods: {
        drawerTitle() {
            if (this.mode == 'print') {
                return this.$t('report.drawer.print_title', { report_name: this.report_params.report_name });
            } else {
                return this.$t('report.drawer.title');
            }
        },
        isFormDirty(isDirty) {
            this.drawer_form_dirty = isDirty;
        },
        onSearch(input) {
            let regex = /^[a-zA-Z0-9\s\(\)_-]+$/;

            if (regex.test(input) || input === '') {
                this.search_value = input;

                if (this.include_inactive_reports === true) {
                    this.$refs.ReportTemplateTable.fetch({
                        include_inactive_reports: this.include_inactive_reports,
                        search_value: this.search_value,
                    });
                } else {
                    this.$refs.ReportTemplateTable.fetch({
                        search_value: this.search_value,
                    });
                }
            } else {
                this.$notification.error({
                    message: this.$t('globals.tables.invalid_characters'),
                });
            }
        },
        onCheckboxChange(event) {
            const value = event.target.checked;

            if (value === true) {
                this.$refs.ReportTemplateTable.fetch({
                    include_inactive_reports: value,
                    search_value: this.search_value,
                });
            } else {
                this.$refs.ReportTemplateTable.fetch({
                    search_value: this.search_value,
                });
            }
        },
        onSelectChange(selected_row_keys) {
            this.selected_row_keys = selected_row_keys;
        },
        onTableAction(data) {
            switch (data.key) {
                case 'edit':
                    this.openDrawer({ id: data.id }, 'edit');
                    break;
                case 'generate':
                    this.openDrawer({ id: data.id }, 'print');
                    break;
                case 'delete':
                    this.onDelete(data.id);
                    break;
            }
        },
        onActionButtons(event, type) {
            switch (event.key) {
                case 'delete':
                    this.onDelete();
                    break;
            }
        },
        onAdd() {
            this.openDrawer({}, 'edit');
        },
        onDelete(report_id = '') {
            let ids = [];

            if (report_id !== '' && report_id !== undefined) {
                ids = [report_id];
            } else if (this.selected_row_keys.length >= 1) {
                ids = this.selected_row_keys;
            }

            if (ids.length >= 1) {
                let that = this;

                this.$confirm({
                    title: this.$t('report.confirm.delete'),
                    content: '',
                    onOk() {
                        that.$http
                            .post('/api/reports/delete', {
                                ids: ids,
                            })
                            .then((response) => {
                                if (response.data.status == 'success') {
                                    that.$notification.success({
                                        message: response.data.message,
                                    });

                                    that.selected_row_keys = [];

                                    that.$refs.ReportTemplateTable.reload();
                                } else {
                                    that.$notification.error({
                                        message: response.data.message,
                                    });
                                }
                            })
                            .catch((error) => {
                                this.$notification.error({
                                    message: this.$t('globals.global.notifications.error.any', { code: 'CVRDEL01' }),
                                });
                            })
                            .finally(() => {
                                that.$refs.ReportTemplateTable.processing = false;
                            });
                    },
                    onCancel() {},
                });
            } else {
                this.$notification.error({
                    message: this.$t('report.notifications.error.select_one_or_more'),
                });
            }
        },
        onExport() {
            this.export_button_processing = true;

            this.$http
                .post('/api/reports/export')
                .then((response) => {
                    if (response.data.status == 'success') {
                        this.$notification.success({
                            message: response.data.message,
                        });

                        this.downloadReport(response.data.file_path, response.data.file_name);
                    } else {
                        this.$notification.error({
                            message: response.data.message,
                        });
                    }
                })
                .catch((error) => {
                    this.$notification.error({
                        message: this.$t('globals.global.notifications.error.any', { code: 'CVREXP01' }),
                    });
                })
                .finally(() => {
                    this.export_button_processing = false;
                });
        },
        openDrawer(record, mode) {
            if (!_.isEmpty(record)) {
                const id = record.id;

                if (mode == 'edit') {
                    this.$http.get('/api/reports/' + id).then((response) => {
                        this.report_template = {
                            ...response.data.data,
                            is_active: response.data.data.is_active ? true : false,
                        };
                        this.mode = mode;
                        this.drawer_visible = true;
                    });
                } else if (mode == 'print') {
                    this.$http.post('/api/reports/' + id + '/params').then((response) => {
                        if (response.data.status == 'success') {
                            this.report_params.id = response.data.id;
                            this.report_params.report_name = response.data.report_name;
                            this.report_params.fields = response.data.fields;
                            this.mode = mode;
                            this.drawer_visible = true;
                        } else {
                            this.$notification.error({
                                message: response.data.message,
                            });
                        }
                    });
                }
            } else {
                this.report_template = {
                    report_name: '',
                    report_type: '',
                    report_module: '',
                    description: '',
                    filename: '',
                    is_active: true,
                };

                this.mode = mode;
                this.drawer_visible = true;
            }
        },
        onDrawerClose() {
            let can_close = true;

            if (this.mode == 'edit') {
                if (this.drawer_form_dirty) {
                    let that = this;
                    can_close = false;

                    this.$confirm({
                        title: this.$t('globals.navigation.notifications.unsaved_changes.title'),
                        content: this.$t('globals.navigation.notifications.unsaved_changes.content'),
                        onOk() {
                            that.drawer_form_dirty = false;
                            that.drawer_visible = false;
                        },
                        onCancel() {},
                    });
                }
            }

            if (can_close) {
                this.drawer_form_dirty = false;
                this.drawer_visible = false;
            }
        },
        onSave(data) {
            if (this.processing) {
                return false;
            }

            this.processing = true;

            const headers = { 'Content-Type': 'multipart/form-data' };

            let payload = data.unformatted_data;

            if (payload.id == undefined) {
                this.$http
                    .post('/api/reports', data.form_data, { headers })
                    .then((response) => {
                        this.drawer_form_dirty = false;

                        this.$notification.success({
                            message: response.data.message,
                        });

                        this.$refs.ReportTemplateTable.sorter = {
                            field: 'created_at',
                            order: 'descend',
                        };

                        this.$refs.ReportTemplateTable.reload();

                        this.drawer_visible = false;
                    })
                    .catch((error) => {
                        if (error.response.status == 422) {
                            this.validation = error.response.data;
                        } else if (error.response.status == 403) {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.403_mod'),
                            });
                        } else {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.any', { code: 'CVRADD01' }),
                            });
                        }
                    })
                    .finally(() => {
                        this.processing = false;
                    });
            } else {
                let id = payload.id;

                this.$http
                    .post('/api/reports/' + id + '/update', data.form_data, { headers })
                    .then((response) => {
                        this.drawer_form_dirty = false;

                        this.$notification.success({
                            message: response.data.message,
                        });

                        this.$refs.ReportTemplateTable.reload();

                        this.drawer_visible = false;
                    })
                    .catch((error) => {
                        if (error.response.status == 422) {
                            this.validation = error.response.data;
                        } else if (error.response.status == 403) {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.403_mod'),
                            });
                        } else {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.any', { code: 'CVRUP01' }),
                            });
                        }
                    })
                    .finally(() => {
                        this.processing = false;
                    });
            }
        },
        onGenerateReport(submitted_data) {
            let data = {};
            let report_id = submitted_data.id;
            let report_data = submitted_data.report_data;

            this.processing = true;

            _.forEach(this.report_params.fields, function (value, key) {
                switch (value.field_type) {
                    case 'date':
                        if (report_data[value.field_name] !== undefined && report_data[value.field_name] !== null) {
                            data[value.field_name] = report_data[value.field_name].format('YYYY-MM-DD');
                        } else {
                            data[value.field_name] = null;
                        }
                        break;
                    case 'date_range':
                        if (report_data[value.field_name] !== undefined) {
                            if (
                                report_data[value.field_name].start_date !== undefined &&
                                report_data[value.field_name].start_date !== null
                            ) {
                                data[value.range.start_date] =
                                    report_data[value.field_name].start_date.format('YYYY-MM-DD');
                            } else {
                                data[value.range.start_date] = null;
                            }

                            if (
                                report_data[value.field_name].end_date !== undefined &&
                                report_data[value.field_name].end_date !== null
                            ) {
                                data[value.range.end_date] =
                                    report_data[value.field_name].end_date.format('YYYY-MM-DD');
                            } else {
                                data[value.range.end_date] = null;
                            }
                        }
                        break;
                    case 'time':
                        if (report_data[value.field_name] !== undefined && report_data[value.field_name] !== null) {
                            data[value.field_name] = report_data[value.field_name].format('hh:mm:ss');
                        } else {
                            data[value.field_name] = null;
                        }
                        break;
                    case 'checkbox':
                        if (report_data[value.field_name] !== undefined && report_data[value.field_name]) {
                            data[value.field_name] = 1;
                        } else {
                            data[value.field_name] = 0;
                        }
                        break;
                    case 'select_multi':
                        if (report_data[value.field_name] !== undefined) {
                            data[value.field_name] = _.join(report_data[value.field_name], ',');
                        } else {
                            data[value.field_name] = [];
                        }
                        break;
                    case 'vehicle_check_upload':
                        break;
                    case 'claim_check_upload':
                        break;
                    default:
                        if (report_data[value.field_name] !== undefined) {
                            data[value.field_name] = report_data[value.field_name];
                        } else {
                            data[value.field_name] = '';
                        }
                        break;
                }
            });

            const payload = new FormData();

            payload.append('id', report_id);

            for (var key in data) {
                payload.append('data[' + key + ']', data[key]);
            }

            if (report_data.claim_check_upload) {
                report_data.claim_check_upload.forEach((file) => {
                    payload.append('data[claim_check_upload][]', file);
                });
            }
            if (report_data.vehicle_check_upload) {
                report_data.vehicle_check_upload.forEach((file) => {
                    payload.append('data[vehicle_check_upload][]', file);
                });
            }

            const headers = { 'Content-Type': 'multipart/form-data' };

            this.$http
                .post('/api/reports/generate', payload, headers)
                .then((response) => {
                    if (response.data.status == 'success') {
                        this.drawer_form_dirty = false;

                        this.$notification.success({
                            message: response.data.message,
                        });

                        this.downloadReport(response.data.file_path, response.data.file_name);

                        this.drawer_visible = false;
                    } else {
                        this.$notification.error({
                            message: response.data.message,
                        });
                    }
                })
                .catch((error) => {
                    if (error.response.status == 422) {
                        this.validation = error.response.data;
                    } else if (error.response.status == 403) {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.403_mod'),
                        });
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVRGEN01' }),
                        });
                    }
                })
                .finally(() => {
                    this.processing = false;
                });
        },
        downloadReport(file_path, file_name) {
            this.$http
                .get(file_path, { responseType: 'arraybuffer' })
                .then((response) => {
                    let blob = new Blob([response.data], { type: response.headers['content-type'] });
                    let link = document.createElement('a');

                    link.href = window.URL.createObjectURL(blob);
                    link.download = file_name;

                    link.click();
                })
                .catch((error) => {
                    // was not able to download file
                    console.log(error);
                });
        },
    },
};
</script>
